import React, { useState, useEffect, useRef } from 'react'
import Title from './micro-components/Title'
import SidebarItem from './micro-components/SidebarItem'

interface SidebarItemProps {
    title: string;
    anchor: string;
}

const Sidebar: React.FC<{
    items: SidebarItemProps[];
}> = ({ items }) => {
    const [activeItem, setActiveItem] = useState<string>('summary');
    const isManualNavigation = useRef(false);
    const navRef = useRef<HTMLDivElement>(null);

    // Function to handle smooth scrolling to section
    const scrollToSection = (anchor: string) => {
        const element = document.getElementById(anchor);
        if (element) {
            // Set manual navigation flag to prevent interference
            isManualNavigation.current = true;
            setActiveItem(anchor);

            element.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });

            // Reset the flag after scroll completes
            setTimeout(() => {
                isManualNavigation.current = false;
            }, 1500);
        }
    };

    // Handle click events to prevent scroll detection interference
    useEffect(() => {
        const handleClickScroll = () => {
            isManualNavigation.current = true;
            setTimeout(() => (isManualNavigation.current = false), 1500);
        };

        const buttons = navRef.current?.querySelectorAll("button[data-section-id]");
        buttons?.forEach((btn) =>
            btn.addEventListener("click", handleClickScroll)
        );

        return () => {
            buttons?.forEach((btn) =>
                btn.removeEventListener("click", handleClickScroll)
            );
        };
    }, [items]);

    // Enhanced section detection with better last section handling
    useEffect(() => {
        const sectionElements = items
            .map((item) => ({
                id: item.anchor,
                element: document.getElementById(item.anchor)
            }))
            .filter((item) => item.element) as { id: string; element: HTMLElement }[];
            
        if (!sectionElements.length) return;
        const lastSectionId = sectionElements[sectionElements.length - 1]?.id;

        let lastActive = activeItem;
        let debounceTimeout: ReturnType<typeof setTimeout> | undefined;

        const updateActiveSection = () => {
            if (isManualNavigation.current) return;
            const firstSection = sectionElements[0];
            if (!firstSection) return;

            const scrollTop = window.scrollY;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            
            // Check if we're near the bottom of the page (within 100px)
            const isNearBottom = scrollTop + windowHeight >= documentHeight - 100;
            
            let newActive = firstSection.id;
            let maxVisibility = 0;

            // Calculate visibility for each section
            sectionElements.forEach(({ id, element }) => {
                const rect = element.getBoundingClientRect();
                const elementTop = rect.top;
                const elementBottom = rect.bottom;
                const elementHeight = rect.height;

                // Calculate how much of the section is visible
                const visibleTop = Math.max(0, -elementTop);
                const visibleBottom = Math.min(elementHeight, windowHeight - elementTop);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                
                // Calculate visibility percentage
                const visibilityPercent = elementHeight > 0 ? visibleHeight / elementHeight : 0;
                
                // Bias towards sections that are closer to the top of viewport
                const topBias = elementTop <= 0 ? 1.2 : 1.0; // Give 20% bonus to sections that have scrolled past top
                const adjustedVisibility = visibilityPercent * topBias;
                
                // Special handling for the last section
                const isLastSection = lastSectionId ? id === lastSectionId : false;
                
                if (isLastSection && isNearBottom) {
                    // Only activate last section if it's significantly visible (>30%) and we're near bottom
                    if (visibilityPercent > 0.3) {
                        newActive = id;
                        return;
                    }
                } else if (adjustedVisibility > maxVisibility && visibilityPercent > 0.1) {
                    // For other sections, use the one with highest adjusted visibility
                    maxVisibility = adjustedVisibility;
                    newActive = id;
                }
            });

            // Additional check: if no section has significant visibility, use traditional approach
            if (maxVisibility < 0.2 && !isNearBottom) {
                for (let i = sectionElements.length - 1; i >= 0; i--) {
                    const section = sectionElements[i];
                    if (!section) continue;
                    const { id, element } = section;
                    if (element.offsetTop <= scrollTop + 200) {
                        newActive = id;
                        break;
                    }
                }
            }

            if (newActive !== lastActive) {
                setActiveItem(newActive);
                lastActive = newActive;
            }
        };

        const observer = new IntersectionObserver(
            (entries) => {
                if (isManualNavigation.current) return;

                // Clear any existing timeout
                if (debounceTimeout) clearTimeout(debounceTimeout);

                // Debounce the update
                debounceTimeout = setTimeout(updateActiveSection, 100);
            },
            {
                root: null,
                rootMargin: "0px",
                threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1.0], // Multiple thresholds for better detection
            }
        );

        sectionElements.forEach(({ element }) => observer.observe(element));

        // Also add scroll listener as backup
        const handleScroll = () => {
            if (debounceTimeout) clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(updateActiveSection, 100);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', handleScroll);
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
    }, [items, activeItem]);

    return (
        <div 
            ref={navRef}
            className="sticky top-[70px] sm:sticky sm:top-[70px] flex flex-col items-start gap-6 p-6 self-stretch bg-white rounded-[6px] sm:p-4"
        >
            <Title title="Content" />
            <div className='flex flex-col gap-2 w-full'>
                {items.map((item, index) => (
                    <SidebarItem 
                        key={index} 
                        title={item.title} 
                        anchor={item.anchor}
                        isActive={activeItem === item.anchor}
                        onClick={() => scrollToSection(item.anchor)}
                        data-section-id={item.anchor}
                    />
                ))}
            </div>
        </div>
    )
}

export default Sidebar